import { apiCall } from '../../../services/apiUtil';

const POKER_SPORT_TYPE_ID = 1;

/**
 * Ensures a poker table record exists for this game.
 * If the game_common has no game_poker_table yet (pokerResults is absent from the
 * API response), we create it now (lazy init) then re-fetch the full game.
 * This handles games that were created via POST /games without the follow-up
 * POST /games/:id/poker/table call.
 */
async function ensurePokerTable(game) {
  if (!game) return null;
  // pokerResults is only present when a poker table exists
  if (game.pokerResults === undefined) {
    // POST is idempotent (find-or-create), safe to call concurrently
    await apiCall(`/games/${game.id}/poker/table`, { method: 'POST' });
    const refreshed = await apiCall(`/games/${game.id}`);
    return adaptGameResponse(refreshed);
  }
  return adaptGameResponse(game);
}

/**
 * Adapts a game_common response (with poker data flattened by gameController)
 * to the shape PokerScoring expects.
 */
function adaptGameResponse(game) {
  if (!game) return null;
  return {
    id: game.id,                        // game_common.id — used for all subsequent API calls
    eventId: game.eventId,
    status: game.status,
    pokerTableStatus: game.pokerTableStatus || 'active',
    // Chip settings now come directly from game_poker_table columns
    chipValue100: game.chipValue100 ?? 100,
    buyinChipCount: game.buyinChipCount ?? 100,
    // Flatten results from pokerResults array
    results: (game.pokerResults || []).map(r => ({
      id: r.id,
      userId: r.userId,
      initialBuyin: r.initialBuyin,
      addonBuyin: r.addonBuyin || 0,
      finalChips: r.finalChips,
      netResult: r.netResult,
      user: r.user
    }))
  };
}

export const pokerGameAPI = {
  /**
   * Create a new poker game:
   * 1. Create game_common with sportTypeId=1 and settings stored as game_settings JSON
   * 2. Create the poker table (lazy init)
   * 3. Return the full game with poker data
   */
  create: async (gameData) => {
    const game = await apiCall('/games', {
      method: 'POST',
      body: JSON.stringify({
        sportTypeId: POKER_SPORT_TYPE_ID,
        eventId: gameData.eventId,
        status: 'in_progress',
      }),
    });

    // Fetch full game — ensurePokerTable will create the poker table if needed
    return ensurePokerTable(game);
  },

  getById: async (id) => {
    try {
      const game = await apiCall(`/games/${id}`);
      return ensurePokerTable(game);
    } catch (error) {
      if (error.status === 404) {
        console.warn('[pokerGameAPI] Game not found (may have been deleted):', id);
        return null; // Caller falls through to event-based lookup or creates fresh
      }
      throw error;
    }
  },

  getByEventId: async (eventId) => {
    const games = await apiCall(`/games/event/${eventId}`);
    const pokerGames = (games || []).filter(g => g.sportTypeId === POKER_SPORT_TYPE_ID);
    return Promise.all(pokerGames.map(ensurePokerTable));
  },

  /**
   * Update game_common status only.
   */
  update: async (id, gameData) => {
    const payload = {};
    if (gameData.status !== undefined) payload.status = gameData.status;
    return apiCall(`/games/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  delete: async (id) => {
    return apiCall(`/games/${id}`, { method: 'DELETE' });
  },

  /**
   * Upsert a player result.
   * The backend resolves game_common_id → poker table, then saves the result.
   */
  /**
   * Update poker table fields: status and/or chip settings.
   * gameId is the game_common.id.
   */
  updateTable: async (gameId, data) => {
    return apiCall(`/games/${gameId}/poker/table`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  upsertResult: async (gameId, userId, resultData) => {
    return apiCall(`/games/${gameId}/poker/table/results/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({
        initialBuyin: resultData.initialBuyin,
        addonBuyin: resultData.addonBuyin || 0,
        finalChips: resultData.finalChips,
      }),
    });
  },

  deleteResult: async (gameId, resultId) => {
    return apiCall(`/games/${gameId}/poker/table/results/${resultId}`, { method: 'DELETE' });
  },

  pause: async (gameId, { reason, durationMinutes }) => {
    return apiCall(`/games/${gameId}/pause`, {
      method: 'POST',
      body: JSON.stringify({ reason, durationMinutes }),
    });
  },

  resume: async (gameId) => {
    return apiCall(`/games/${gameId}/resume`, { method: 'POST' });
  },
};

export default pokerGameAPI;
